import {
  Spacing,
  theme as defaultTheme,
  ExpandableSection,
} from "@mindlercare/ui-web";
import ConfirmButtonWithDialog from "components/lib/confirmButtonWithDialog";
import { Typography } from "components/typography";
import { Trans, useTranslation } from "react-i18next";
import { styled } from "styled-components";

const ContentSection = styled.div`
  margin-top: ${defaultTheme.SPACING.MD};
  display: flex;
  flex-direction: column;
`;

const PreviewText = styled.div`
  font-size: ${defaultTheme.FONT.SIZE.SMALL_MEDIUM};
  font-family: ${defaultTheme.FONT.FAMILY.BODY};
  color: ${defaultTheme.COLORS.GRAY_DARK};
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
`;

const Highlight = styled.span`
  color: ${defaultTheme.COLORS.ERROR};
  font-weight: ${defaultTheme.FONT.WEIGHT.MEDIUM};
`;

const SummaryContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${defaultTheme.SPACING.SM};
`;

interface SimilarityConfirmModalProps {
  open: boolean;
  onClose: (confirmed: boolean) => void;
  generatedDraftText: string;
  feedbackInput: string;
  similarityScore: number | null;
  isSending?: boolean;
}

export const SimilarityConfirmModal = ({
  open,
  onClose,
  generatedDraftText,
  feedbackInput,
  similarityScore,
  isSending,
}: SimilarityConfirmModalProps) => {
  const { t } = useTranslation();

  return (
    <ConfirmButtonWithDialog
      open={open}
      handleClose={onClose}
      title={t("LLM_AI_FEEDBACK.SIMILARITY_CONFIRM_TITLE")}
      description=""
      confirmText={t("SEND_FEEDBACK")}
      cancelText={t("CANCEL")}
      confirmDisabled={isSending}
      maxWidth="600px"
    >
      <Typography variant="body" color="GRAY_DARK">
        <Trans
          i18nKey="LLM_AI_FEEDBACK.SIMILARITY_CONFIRM_DESCRIPTION_WITH_SCORE"
          values={{
            score: t(
              (similarityScore ?? 0) >= 0.95
                ? "LLM_AI_FEEDBACK.SIMILARITY_VERY_HIGH"
                : "LLM_AI_FEEDBACK.SIMILARITY_HIGH",
            ),
          }}
          components={{ highlight: <Highlight /> }}
        />
      </Typography>
      <ContentSection>
        <ExpandableSection expanded={false}>
          <ExpandableSection.Toggle>
            <SummaryContent>
              <Typography variant="body-small-bold" color="GRAY_DARK">
                {t("LLM_AI_FEEDBACK.SIMILARITY_CONFIRM_YOUR_TEXT_LABEL")}
              </Typography>
            </SummaryContent>
          </ExpandableSection.Toggle>
          <ExpandableSection.Content>
            <Spacing t={12} />
            <PreviewText>{feedbackInput}</PreviewText>
          </ExpandableSection.Content>
        </ExpandableSection>
        <ExpandableSection expanded={false}>
          <ExpandableSection.Toggle>
            <Typography variant="body-small-bold" color="GRAY_DARK">
              {t("LLM_AI_FEEDBACK.SIMILARITY_CONFIRM_AI_DRAFT_LABEL")}
            </Typography>
          </ExpandableSection.Toggle>
          <ExpandableSection.Content>
            <Spacing t={12} />
            <PreviewText>{generatedDraftText}</PreviewText>
          </ExpandableSection.Content>
        </ExpandableSection>
      </ContentSection>
    </ConfirmButtonWithDialog>
  );
};
